import { z } from 'zod';
import { IpAddress, IsoTime } from './basics.js';
import { CaptchaKey, CaptchaKeys, CaptchaProvider } from './command.js';
/**
 * Outside services Bartizo holds a secret for (P11.9.3, P11.9.5, P11.9.6): the
 * captcha keys the Nginx bouncer's challenge shows, the Cloudflare token that keeps
 * the edge's IP list, and the CrowdSec CTI key for an address's reputation.
 *
 * Each secret is sealed in the API (`integrations.secret_enc`, S25) and never sent
 * back to the browser — a view says only whether one is set. The captcha keys alone
 * leave central, to approved agents, which hand them to the helper for the
 * bouncer's own file (rule 5: not in a settings document, not on a command line).
 */
export const IntegrationName = z.enum(['captcha', 'cloudflare', 'crowdsec_cti']);
const Who = { updatedAt: IsoTime, updatedByEmail: z.email().nullable() };
/** A token or key as the services issue them: no spaces, no control characters. */
const ServiceSecret = z
    .string()
    .trim()
    .min(20)
    .max(200)
    .regex(/^[A-Za-z0-9_.-]+$/, 'letters, digits, dot, dash and underscore only');
// --- Captcha (P11.9.3, decision C6) ---------------------------------------------
export const CaptchaView = z.object({
    provider: CaptchaProvider,
    siteKey: CaptchaKey,
    hasSecret: z.boolean(),
    ...Who,
});
export const CaptchaInput = z
    .object({
    provider: CaptchaProvider,
    siteKey: CaptchaKey,
    /** Absent keeps the one stored. */
    secretKey: CaptchaKey.optional(),
})
    .strict();
/** `GET /v1/agent/captcha`: the keys, or null when none are set. */
export const AgentCaptcha = z.object({ keys: CaptchaKeys.nullable() });
// --- Cloudflare (P11.9.5, decision C8) ------------------------------------------
export const CloudflareId = z.string().regex(/^[a-f0-9]{32}$/, 'a Cloudflare id');
export const CloudflareZone = z.object({
    id: CloudflareId,
    name: z.string().min(1).max(253),
});
/** What the edge does to an address on the list. */
export const CloudflareAction = z.enum(['block', 'managed_challenge']);
/** The account's IP list Bartizo keeps. Cloudflare list names: lower case, digits, `_`. */
export const CLOUDFLARE_LIST_NAME = 'bartizo_blocklist';
/** The free plan's list size; the most recently active addresses first past it. */
export const CLOUDFLARE_LIST_MAX = 10_000;
export const CloudflareConfig = z.object({
    enabled: z.boolean(),
    accountId: CloudflareId,
    action: CloudflareAction,
    /** The zones whose custom rules refer to the list; Bartizo adds its one rule to each. */
    zones: z.array(CloudflareZone).max(100),
    /** Bans from these servers' CrowdSec go to the edge too, beside the fleet list. */
    includeServerBans: z.boolean(),
});
export const CloudflareSyncState = z.object({
    syncedAt: IsoTime.nullable(),
    addresses: z.number().int().nonnegative(),
    truncated: z.boolean(),
    listId: z.string().max(64).nullable(),
    /** Zones whose rule is in place. */
    rules: z.array(z.string().max(253)).max(100),
    error: z.string().max(500).nullable(),
});
export const CloudflareView = CloudflareConfig.extend({
    hasToken: z.boolean(),
    sync: CloudflareSyncState,
    ...Who,
});
export const CloudflareInput = CloudflareConfig.extend({
    /** Absent keeps the one stored. Scoped to Account Filter Lists: Edit and Zone WAF: Edit. */
    token: ServiceSecret.optional(),
}).strict();
/** `POST /v1/integrations/cloudflare/zones`: the zones a token reaches, to choose from. */
export const CloudflareZonesRequest = z
    .object({ accountId: CloudflareId, token: ServiceSecret.optional() })
    .strict();
export const CloudflareZonesResponse = z.object({ zones: z.array(CloudflareZone).max(500) });
// --- CrowdSec CTI (P11.9.6, decision C9) ----------------------------------------
export const CtiConfig = z.object({ enabled: z.boolean() });
export const CtiView = CtiConfig.extend({ hasKey: z.boolean(), ...Who });
export const CtiInput = CtiConfig.extend({ key: ServiceSecret.optional() }).strict();
/** How long an answer is kept before CrowdSec is asked again. */
export const CTI_CACHE_SECONDS = 7 * 24 * 3600;
const Label = z.object({ name: z.string().max(200), label: z.string().max(200) });
/** What CrowdSec says of one address — advice for a person, never a ban (rule 11). */
export const CtiReputation = z.object({
    ip: IpAddress,
    /** `unknown` when CrowdSec has never seen it attack. */
    reputation: z.string().max(40),
    classifications: z.array(Label).max(30),
    falsePositives: z.array(Label).max(30),
    behaviors: z.array(Label).max(30),
    /** Scenarios it was seen in, newest CrowdSec knows first. */
    attackDetails: z.array(Label).max(30),
    firstSeen: IsoTime.nullable(),
    lastSeen: IsoTime.nullable(),
    /** 0–5, CrowdSec's overall score. */
    score: z.number().min(0).max(5).nullable(),
    backgroundNoise: z.number().min(0).max(10).nullable(),
    asName: z.string().max(200).nullable(),
    country: z.string().max(10).nullable(),
});
/** `GET /v1/addresses/:ip/reputation`. */
export const CtiLookup = z.object({
    state: z.enum(['found', 'unknown', 'not_configured', 'quota', 'error']),
    reputation: CtiReputation.nullable(),
    fetchedAt: IsoTime.nullable(),
    /** From the cache rather than CrowdSec just now. */
    cached: z.boolean(),
    detail: z.string().max(300).nullable(),
});
/** `GET /v1/integrations`: each one's view, or null when it is not set up. */
export const IntegrationsView = z.object({
    captcha: CaptchaView.nullable(),
    cloudflare: CloudflareView.nullable(),
    cti: CtiView.nullable(),
});
//# sourceMappingURL=integrations.js.map